import React, { useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';

const RichTextEditor = forwardRef(({
  value,
  onChange,
  placeholder = "Enter description...",
  maxLength = 3000,
  blockWhenMaxExceeded = true,
  height = "200px",
  error = false,
  disabled = false,
  className = "",
  onBlur,
  onLengthChange
}, ref) => {
  const internalQuillRef = useRef(null);

  // Expose ReactQuill instance to the forwarded ref
  useImperativeHandle(ref, () => internalQuillRef.current);

  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  }), []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'color', 'background',
    'align'
  ];

  // Calculate length using Quill's built-in method on mount or when value changes externally (e.g. existing job)
  useEffect(() => {
    if (internalQuillRef.current) {
      const editor = internalQuillRef.current.getEditor?.();
      if (editor) {
        const text = editor.getText?.() || '';
        const plainTextLength = text.trim().length;
        onLengthChange?.(plainTextLength, text, editor);
      }
    }
  }, [value, onLengthChange]);

  const handleChange = (content, delta, source, editor) => {
    const text = editor?.getText?.() || '';
    const plainTextLength = text.trim().length;

    // Block additional input after the limit and remove excess characters
    if (blockWhenMaxExceeded && typeof maxLength === 'number' && plainTextLength > maxLength) {
      const extraChars = plainTextLength - maxLength;
      const realQuill = internalQuillRef.current?.getEditor?.();

      if (realQuill && extraChars > 0 && typeof realQuill.deleteText === 'function') {
        const sel = realQuill.getSelection ? realQuill.getSelection() : (editor?.getSelection ? editor.getSelection() : null);
        if (sel && typeof sel.index === 'number' && sel.index >= extraChars) {
          realQuill.deleteText(sel.index - extraChars, extraChars, 'silent');
        } else {
          const rawDocText = (realQuill.getText ? realQuill.getText() : text).replace(/\n$/, '');
          const startIndex = Math.max(0, rawDocText.length - extraChars);
          realQuill.deleteText(startIndex, extraChars, 'silent');
        }
      }

      // Only show warning toast when user is actively entering or pasting text
      if (source === 'user') {
        toast.error(`Character limit of ${maxLength.toLocaleString()} reached. Further text is not allowed.`, {
          id: 'editor-char-limit-warning'
        });
      }

      const updatedText = realQuill?.getText?.() || editor?.getText?.() || '';
      const updatedLength = updatedText.trim().length;
      onLengthChange?.(updatedLength, updatedText, realQuill || editor);
      onChange?.(realQuill?.root?.innerHTML || content, delta, source, editor);
      return;
    }

    onLengthChange?.(plainTextLength, text, editor);
    onChange?.(content, delta, source, editor);
  };

  return (
    <div
      className={`rich-text-editor ${className}`}
      style={{
        height: height,
        marginBottom: '42px' // Space for toolbar
      }}
    >
      <ReactQuill
        ref={internalQuillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        onBlur={onBlur}
        style={{
          height: '100%'
        }}
      />
      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;

